import { Sticky } from './sticky';
import { Text } from './text';
import { Shape } from './shape';
import { Freedraw } from './freedraw';
import { Frame } from './frame';
import { Emoji } from './emoji';
import { Gif } from './gif';
import { ConnectedEntityProxyNode } from '@/features/canvas/components/shell/connected-entity-proxy-node';
import type { EntityProxyData } from '@/features/canvas/engine/schema/types';

// ── EntityProxy registry entry ────────────────────────────────────────────────
// M5: entity-proxy lives as flat engine/nodes files (EntityProxyNodeView.tsx),
// not a per-type folder. The shell wrapper (ConnectedEntityProxyNode) is the
// React Flow component. The cross-layer import here is intentional — the
// nodeTypeMap IS the seam. Task 12's audit checks EntityProxyNodeView.tsx's
// import graph, NOT this file.

const ENTITY_PROXY_DEFAULTS: EntityProxyData = {
  entityKind: 'document',
  entityId: null,
  isVirtual: false,
  virtualTitle: null,
  cachedTitle: '',
  cachedSnippet: null,
  cachedUpdatedAt: 0,
  tentative: false,
  displayMode: 'card',
  showPreview: true,
};

function isValidEntityProxyData(value: unknown): value is EntityProxyData {
  if (typeof value !== 'object' || value === null) return false;
  const v = value as Record<string, unknown>;
  return (
    typeof v.entityKind === 'string' &&
    (v.entityId === null || typeof v.entityId === 'string') &&
    typeof v.isVirtual === 'boolean' &&
    (v.virtualTitle === null || typeof v.virtualTitle === 'string') &&
    typeof v.cachedTitle === 'string' &&
    typeof v.tentative === 'boolean'
  );
}

const EntityProxy = {
  Component: ConnectedEntityProxyNode,
  makeDefault: () => ({ ...ENTITY_PROXY_DEFAULTS }),
  isValidData: isValidEntityProxyData,
} as const;

/**
 * Central node-type registry. One source of truth for all currently-implemented
 * node types. Everything below derives from this map.
 *
 * Adding a new node type = create a folder + add one line here.
 */
export const NODE_DEFINITIONS = {
  sticky: Sticky,
  text: Text,
  shape: Shape,
  freedraw: Freedraw,
  frame: Frame,
  'entity-proxy': EntityProxy,
  emoji: Emoji,
  gif: Gif,
} as const;

/** Derives from NODE_DEFINITIONS keys — automatically widens when new kinds register. */
export type RegistryNodeType = keyof typeof NODE_DEFINITIONS;

/** All registered node type values as a runtime array. */
export const NODE_TYPES = Object.keys(NODE_DEFINITIONS) as RegistryNodeType[];

/**
 * React Flow nodeTypes prop — derived from NODE_DEFINITIONS.
 * Replaces the hand-maintained nodeTypeMap.ts.
 */
export const nodeTypeMap = Object.fromEntries(
  Object.entries(NODE_DEFINITIONS).map(([k, v]) => [k, v.Component])
) as { [K in RegistryNodeType]: typeof NODE_DEFINITIONS[K]['Component'] };

/** Type-safe data type for a given registered node type. */
export type NodeDataFor<T extends RegistryNodeType> =
  ReturnType<typeof NODE_DEFINITIONS[T]['makeDefault']>;

/**
 * Discriminated union of all registered node data payloads.
 */
export type AnyNodeData = {
  [K in RegistryNodeType]: { type: K; data: NodeDataFor<K> }
}[RegistryNodeType];

/**
 * Type-safe default-data factory. Returns the correct data shape for any
 * registered node type — no switch statement, no case maintenance.
 */
export function defaultDataForType<T extends RegistryNodeType>(type: T): NodeDataFor<T> {
  return NODE_DEFINITIONS[type].makeDefault() as NodeDataFor<T>;
}

/**
 * Runtime validator dispatch — returns true if `data` is a valid payload for
 * the given node type.
 */
export function isValidNodeData(type: RegistryNodeType, data: unknown): boolean {
  return NODE_DEFINITIONS[type].isValidData(data);
}

// ── Re-exports from per-type folders ──────────────────────────────────────────
// Consumers that previously imported from engine/nodes/index.ts (the old barrel)
// can continue to do so for the legacy named exports.
export { Sticky } from './sticky';
export type { StickyData, StickyNodeData } from './sticky';
export { Text } from './text';
export type { TextData, TextNodeData } from './text';
export { Shape } from './shape';
export type { ShapeData, ShapeNodeData, ShapeKind } from './shape';
export { SHAPE_COMPONENTS } from './shape';
export { Freedraw } from './freedraw';
export type { FreedrawData, FreedrawNodeData } from './freedraw';
export { Frame } from './frame';
export type { FrameData, FrameNodeData } from './frame';
export { Emoji } from './emoji';
export type { EmojiData } from './emoji';
export { Gif } from './gif';
export type { GifData } from './gif';